import { ElementProps } from '@/components/sections/builder/types';
import { HeaderElementDataType } from '@/components/sections/builder/elements/header/header/type';
import HeaderMenuSetting from '@/components/sections/builder/settings/header-menu-setting';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/shallow';
import InputTab from '@/components/sections/builder/settings/input-tab';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { defaultData } from '@/components/sections/builder/elements/header/header';

const logoPositionOptions = [
  { label: 'Top Left', value: 'top-left', asset: '/assets/elements/top-left.svg' },
  { label: 'Top Center', value: 'top-center', asset: '/assets/elements/top-center.svg' },
  { label: 'Middle Left', value: 'middle-left', asset: '/assets/elements/middle-left.svg' },
  {
    label: 'Middle Center',
    value: 'middle-center',
    asset: '/assets/elements/middle-center.svg',
  },
];

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as HeaderElementDataType;

  return (
    <div>
      {/* Content Section */}
      <div className='border-b pb-4'>
        <h4 className='mb-4 text-sm font-semibold'>Content</h4>
        <HeaderMenuSetting
          value={
            data.menuId
              ? { value: data.menuId as string, label: data.menuLabel as string }
              : undefined
          }
          onChange={(val) =>
            builder.setElementData(element.id, {
              menuId: val?.value || '',
              menuLabel: val?.label || '',
            })
          }
        />
        <div className='mb-4 mt-4 font-bold'>Logo</div>
        <ImageSetting
          value={data?.logoUrl}
          onChange={(val) =>
            builder.setElementData(element.id, {
              logoUrl: val,
            })
          }
        />
        <div className='mt-4 flex items-center gap-x-2'>
          <Checkbox
            id='showSearch'
            checked={data.showSearch}
            onCheckedChange={(checked) => {
              builder.setElementData(element.id, {
                showSearch: !!checked,
              });
            }}
          />
          <label htmlFor='showSearch' className='text-sm'>
            Show search
          </label>
        </div>
        <div className='mb-4 mt-4 font-bold'>Search style</div>
        <InputTab
          grid={2}
          defaultValue={data.searchBarPosition ?? defaultData.searchBarPosition}
          options={[
            { label: 'Icon', value: 'modal' },
            { label: 'Searchbar', value: 'header' },
          ]}
          onChange={(v) => builder.setElementData(element.id, { searchBarPosition: v })}
        />
        <p className='mt-2 text-xs text-muted-foreground'>
          If the Logo Position is Middle-Left, the style will be an icon in Desktop.
        </p>
      </div>

      {/* Layout Section */}
      <div className='border-b py-4'>
        <h4 className='mb-4 text-sm font-semibold'>Layout</h4>
        <div className='mb-4 font-bold'>Sticky Header</div>
        <Select
          name='stickyHeader'
          value={data.stickyHeader ?? defaultData.stickyHeader}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              stickyHeader: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='none'>None</SelectItem>
            <SelectItem value='on-scroll-up'>On Scroll Up</SelectItem>
            <SelectItem value='always'>Always</SelectItem>
          </SelectContent>
        </Select>

        <div className='mb-4 mt-4 font-bold'>Logo Position</div>
        <div className='mb-5 grid grid-cols-2 gap-4'>
          {logoPositionOptions.map((option) => (
            <div
              key={option.value}
              role='button'
              onClick={() =>
                builder.setElementData(element.id, {
                  logoPosition: option.value,
                })
              }
              className={cn(
                'flex flex-col items-center rounded-md border p-2',
                option.value === data.logoPosition && 'border-2 border-primary'
              )}
            >
              <Image
                src={option.asset}
                alt={option.label}
                width={100}
                height={100}
                className='h-full w-full object-contain'
              />
              <span className='mt-2 text-xs text-gray-600'>{option.label}</span>
            </div>
          ))}
        </div>

        <div className='mb-4 mt-4 font-bold'>Desktop menu position</div>
        <Select
          name='desktopMenuPosition'
          value={data.desktopMenuPosition ?? defaultData.desktopMenuPosition}
          onValueChange={(val) =>
            builder.setElementData(element.id, {
              desktopMenuPosition: val,
            })
          }
        >
          <SelectTrigger className='py-5'>
            <SelectValue placeholder='Select' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='left'>Left</SelectItem>
            <SelectItem value='center'>Center</SelectItem>
          </SelectContent>
        </Select>
        <p className='mt-1 text-xs text-muted-foreground'>
          This setting only works if logo position is Top Left or Top Center.
        </p>
        {data.logoPosition === 'middle-left' && (
          <>
            <div className='mb-4 mt-4 font-bold'>Middle Left Menu Position</div>
            <Select
              name='MenuPosition'
              value={data.middleLeftMenuPosition || 'center'}
              onValueChange={(val) =>
                builder.setElementData(element.id, {
                  middleLeftMenuPosition: val,
                })
              }
            >
              <SelectTrigger className='py-5'>
                <SelectValue placeholder='Select' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='left'>Left</SelectItem>
                <SelectItem value='center'>Center</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <div className='mb-4 mt-4 flex flex-col gap-2'>
          <span className='font-bold'>Style width</span>
          <InputTab
            grid={2}
            defaultValue={data.styleWidth ?? defaultData.styleWidth}
            options={[
              { label: 'Full Width', value: 'full' },
              { label: 'Box', value: 'box' },
            ]}
            onChange={(v) => builder.setElementData(element.id, { styleWidth: v })}
          />
        </div>
      </div>

      {/* Style Section */}
      <div className='py-4'>
        <h4 className='mb-4 text-sm font-semibold'>Style</h4>
        <div className='mt-4 flex items-center gap-x-2'>
          <Checkbox
            id='megaMenuImageOverlay'
            checked={data.megaMenuImageOverlay}
            onCheckedChange={(checked) => {
              builder.setElementData(element.id, {
                megaMenuImageOverlay: !!checked,
              });
            }}
          />
          <label htmlFor='megaMenuImageOverlay' className='text-sm'>
            Mega menu image overlay
          </label>
        </div>
        <div className='mb-2 mt-4 flex flex-col'>
          <span className='font-bold'>Spacing navigation</span>
          <div className='w-full'>
            <InputSlider
              value={data.spacingNav || 0}
              min={0}
              max={50}
              onChange={(val) => {
                builder.setElementData(element.id, {
                  spacingNav: val,
                });
              }}
            />
          </div>
        </div>
        <div className='mb-4 mt-4 font-bold'>Color</div>
        <div>
          <h4 className='my-2'>Menu Color Scheme</h4>
          <SchemeSetting
            value={data.colorSchemeHeader}
            onChange={(theme) => {
              builder.setElementData(element.id, {
                colorSchemeHeader: theme,
              });
            }}
          />
        </div>
        <div>
          <h4 className='my-2'>Nav Color Scheme</h4>
          <SchemeSetting
            value={data.colorSchemeMenuHeader}
            onChange={(theme) => {
              builder.setElementData(element.id, {
                colorSchemeMenuHeader: theme,
              });
            }}
          />
        </div>
        <div className='mb-2 mt-4 font-bold'>Spacing</div>
        <div className='flex items-center'>
          <div className='w-16 text-xs'>Bottom</div>
          <div className='w-full'>
            <InputSlider
              min={0}
              value={data.spacing || 0}
              onChange={(val) => {
                builder.setElementData(element.id, {
                  spacing: val,
                });
              }}
            />
          </div>
        </div>
        <PaddingSetting
          value={data.padding}
          hideLeft={data.styleWidth === 'box'}
          hideRight={data.styleWidth === 'box'}
          onChange={(v) =>
            builder.setElementData(element.id, {
              padding: v,
            })
          }
        />
      </div>
    </div>
  );
}
